import type { Condition, SearchQueryBody, SortDirection } from "@/types";

/**
 * One condition that holds when every one of `conditions` holds: `undefined`
 * for none, the condition itself for one, and an `AND` of them for several.
 */
export function andConditions(
  conditions: readonly Condition[] | undefined,
): Condition | undefined {
  if (!conditions || conditions.length === 0) {
    return undefined;
  }
  if (conditions.length === 1) {
    return conditions[0];
  }
  return { operator: "AND", conditions: [...conditions] };
}

/**
 * Assembles a `SearchQueryBody` from already-translated `Condition`s.
 * The scope and filter conditions are joined under an outer `AND`.
 */
export function composeQueryBody(args: {
  scopeCondition: Condition | undefined;
  filterCondition: Condition | undefined;
  sort: { columnId: string; direction: "asc" | "desc" } | null | undefined;
  /**
   * Total-results cap, emitted as `max_results` on the body (distinct from `limit`,
   * the page size). Omit for the default uncapped behavior.
   */
  maxResults?: number;
}): SearchQueryBody {
  const { scopeCondition, filterCondition, sort, maxResults } = args;
  const condition = andConditions(
    [scopeCondition, filterCondition].filter((c) => c !== undefined),
  );

  return {
    ...(condition && { condition }),
    ...(sort && {
      sort_by: sort.columnId,
      sort_direction: sort.direction.toUpperCase() as SortDirection,
    }),
    ...(maxResults !== undefined && { max_results: maxResults }),
  };
}
